import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Clock, Play, Square } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import type { AssistantTimeEntry, Task } from "@shared/schema";

interface TimeEntryWithDetails extends AssistantTimeEntry {
  task?: { id: string; title: string } | null;
  goal?: { id: string; title: string } | null;
}

interface AssistantProfile {
  id: string;
  userId: string;
  managerUserId: string;
  permissions: {
    notes: boolean;
    goals: boolean;
    calendar: boolean;
    tasks: boolean;
  };
}

export function TimeClockButton() {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [elapsedTime, setElapsedTime] = useState<string>("00:00:00");
  const [description, setDescription] = useState("");
  const [selectedTaskId, setSelectedTaskId] = useState<string>("");

  const { data: assistantProfile } = useQuery<AssistantProfile | null>({
    queryKey: ["/api/assistants/profile"],
  });

  const { data: activeEntry } = useQuery<TimeEntryWithDetails | null>({
    queryKey: ["/api/time-entries/active"],
    enabled: !!assistantProfile,
  });

  const { data: tasks = [] } = useQuery<Task[]>({
    queryKey: ["/api/tasks"],
    enabled: !!assistantProfile && isOpen,
  });

  const { data: sharedTasks = [] } = useQuery<Task[]>({
    queryKey: ["/api/shared-tasks"],
    enabled: !!assistantProfile && isOpen,
  });

  const allTasks = [...tasks, ...sharedTasks].filter(t => !t.completed);

  const clockInMutation = useMutation({
    mutationFn: (data: { taskId?: string; description?: string }) =>
      apiRequest("POST", "/api/time-entries/clock-in", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/time-entries/active"] });
      queryClient.invalidateQueries({ queryKey: ["/api/time-entries"] });
      setDescription("");
      setSelectedTaskId("");
      setIsOpen(false);
      toast({
        title: "Clocked In",
        description: "Timer started successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Clock In Failed",
        description: error?.message || "Failed to start timer.",
        variant: "destructive",
      });
    },
  });

  const clockOutMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `/api/time-entries/${id}/clock-out`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/time-entries/active"] });
      queryClient.invalidateQueries({ queryKey: ["/api/time-entries"] });
      setIsOpen(false);
      toast({
        title: "Clocked Out",
        description: "Timer stopped successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Clock Out Failed",
        description: error?.message || "Failed to stop timer.",
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (!activeEntry?.clockInAt) {
      setElapsedTime("00:00:00");
      return;
    }

    const updateElapsed = () => {
      const start = new Date(activeEntry.clockInAt).getTime();
      const now = Date.now();
      const diff = now - start;

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setElapsedTime(
        `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`
      );
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);

    return () => clearInterval(interval);
  }, [activeEntry?.clockInAt]);

  const handleClockIn = () => {
    if (!selectedTaskId || selectedTaskId === "none") {
      toast({
        title: "Task Required",
        description: "Please select a task to track time against.",
        variant: "destructive",
      });
      return;
    }
    clockInMutation.mutate({
      taskId: selectedTaskId,
      description: description || undefined,
    });
  };

  const handleClockOut = () => {
    if (activeEntry) {
      clockOutMutation.mutate(activeEntry.id);
    }
  };

  if (!assistantProfile) {
    return null;
  }

  return (
    <>
      <Button
        variant={activeEntry ? "default" : "outline"}
        size="sm"
        onClick={() => setIsOpen(true)}
        className={activeEntry ? "bg-green-600 hover:bg-green-700 text-white" : ""}
        data-testid="button-time-clock"
      >
        <Clock className="w-4 h-4 mr-2" />
        {activeEntry ? elapsedTime : "Clock In"}
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Time Tracking
            </DialogTitle>
            <DialogDescription>
              {activeEntry ? "You are currently clocked in" : "Start tracking your work time"}
            </DialogDescription>
          </DialogHeader>

          {activeEntry ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Badge variant="default" className="bg-green-600 hover:bg-green-700">
                  <Clock className="w-3 h-3 mr-1" />
                  Clocked In
                </Badge>
                <span className="text-xs text-muted-foreground">
                  Started {formatDistanceToNow(new Date(activeEntry.clockInAt), { addSuffix: true })}
                </span>
              </div>

              <div className="text-center py-6 bg-muted/50 rounded-lg">
                <div className="text-4xl font-mono font-bold text-primary" data-testid="elapsed-time-dialog">
                  {elapsedTime}
                </div>
                <p className="text-sm text-muted-foreground mt-2">Elapsed Time</p>
              </div>

              {activeEntry.task && (
                <div className="p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Task:</span>
                    <span className="font-medium">{activeEntry.task.title}</span>
                  </div>
                </div>
              )}

              <Button
                onClick={handleClockOut}
                disabled={clockOutMutation.isPending}
                className="w-full"
                variant="destructive"
                data-testid="button-clock-out-dialog"
              >
                <Square className="w-4 h-4 mr-2" />
                {clockOutMutation.isPending ? "Stopping..." : "Stop Timer"}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="clock-in-task">Select Task *</Label>
                <Select value={selectedTaskId} onValueChange={setSelectedTaskId}>
                  <SelectTrigger data-testid="select-task-dialog">
                    <SelectValue placeholder="Choose a task to track time..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allTasks.length === 0 ? (
                      <SelectItem value="none" disabled>No tasks available</SelectItem>
                    ) : (
                      allTasks.map((task) => (
                        <SelectItem key={task.id} value={task.id}>
                          {task.title}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Time entries must be linked to a task
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="clock-in-description">Notes (Optional)</Label>
                <Textarea
                  id="clock-in-description"
                  placeholder="What will you be working on?"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  data-testid="input-description-dialog"
                />
              </div>

              <Button
                onClick={handleClockIn}
                disabled={clockInMutation.isPending || !selectedTaskId || selectedTaskId === "none"}
                className="w-full"
                data-testid="button-clock-in-dialog"
              >
                <Play className="w-4 h-4 mr-2" />
                {clockInMutation.isPending ? "Starting..." : "Start Timer"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
